import gql from 'graphql-tag';
import Interface from '../Interface';
import { ListingConnection, mergeListingConnectionAliases } from '../Listing';
import {
  getMeUserFields,
  profileUserFields,
  userAccountFields,
  userFields,
} from './UserFields';
import {
  MemberSchema,
  RoleSchema,
  UserSchema,
} from '../../definitions/schemas';
import { searchUsersQuery } from './UserQueries';
import { Order } from '../../enums/Order';
import { FilterOperators } from '../../enums/FilterOperators';

export interface UpdateProfileInput {
  firstName: string;
  id?: string;
  jobTitle?: string;
  lastName: string;
  linkedin?: string;
  phoneNumber?: string;
  photo?: string;
  role?: string;
  roleDescription?: string;
  teamId?: string;
}

interface UpdateProfileData {
  updateProfile: {
    user: UserSchema;
  };
}

export interface UpdateUserInput {
  email: string;
  firstName: string;
  id?: string;
  jobTitle?: string;
  lastName: string;
  linkedin?: string;
  phoneNumber?: string;
  photo?: string;
  role?: string;
  roleDescription?: string;
  subscribedToMarketing?: boolean;
  teamId?: string;
}

interface UpdateUserData {
  updateUser: {
    user: UserSchema;
  };
}

interface GetMeData {
  me: UserSchema;
}

interface GetForAccountData {
  me: UserSchema;
}

export interface InviteUsersInput {
  email: string;
  firstName: string;
  lastName: string;
  roleId: number;
  teamId?: string;
}

interface GetWelcomeMessageCoverData {
  me: UserSchema;
}

interface GetRolesData {
  roles: RoleSchema[];
}

interface InviteUsersData {
  inviteUsers: {
    invited: boolean;
  };
}

interface SearchUsersInput {
  first: number;
  ids?: string[];
  search?: string;
  searchFields?: string[];
  status?: string;
  sortBy?: string;
  sortDirection?: string;
  filters?: {
    field: string;
    operation: FilterOperators;
    value: string;
  }[];
  sort?: {
    field: string;
    direction: Order;
  }[];
}

interface SearchUsersData {
  allUsers: ListingConnection<UserSchema>;
  usersByIds: ListingConnection<UserSchema>;
}

export default class UserInterface extends Interface {
  public changePassword(password: string) {
    return this.apolloService.executeMutation({
      mutation: gql`
        mutation changePassword($input: ChangePasswordInput!) {
          changePassword(input: $input) {
            details
            errors
          }
        }
      `,
      variables: {
        input: {
          password,
        },
      },
    });
  }

  public async updateProfile(
    input: UpdateProfileInput,
    fields = profileUserFields
  ) {
    return this.mutate<UpdateProfileData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateProfile($input: UpdateProfileInput!) {
            updateProfile(input: $input) {
              errors
              user {
                ${fields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.updateProfile;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating user profile');
  }

  public async updateUser(input: UpdateUserInput, userFields = '') {
    return this.mutate<UpdateUserData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation UpdateUser($input: UpdateUserInput!) {
            updateUser(input: $input) {
              errors
              user {
                ${userFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.updateUser;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating user');
  }

  public setStatus(id: number, active: boolean) {
    return this.apolloService.executeMutation({
      mutation: gql`
        mutation updateUserStatus($input: UpdateUserStatusInput!) {
          updateUserStatus(input: $input) {
            errors
            details
          }
        }
      `,
      variables: {
        input: { id, active },
      },
    });
  }

  /**
   * Retrieve session data mostly for setting up the user contexts
   * @warning Keep the session data lean. Avoid loading:
   *          - Stripe information
   *          - CRM integration logs
   */
  public async getMe(): Promise<UserSchema> {
    const response = await this.query(
      () =>
        this.apolloService.executeQuery<GetMeData>({
          query: gql`
            query me {
              me {
                ${getMeUserFields}
              }
            }
          `,
        }),
      'Error loading user'
    );

    return response.me;
  }

  public async getForAccount(email: string) {
    const data = await this.query<GetForAccountData>(
      () =>
        this.apolloService.executeQuery<GetForAccountData>({
          query: gql`
            query me {
              me {
                ${userAccountFields}
              }
            }
          `,
          variables: {
            email,
          },
        }),
      'Error loading user'
    );
    return data.me;
  }

  public async inviteUsers(input: InviteUsersInput[]) {
    return this.mutate<InviteUsersData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation inviteUsers($input: InviteUsersInput!) {
            inviteUsers(input: $input) {
              errors
              invited
            }
          }
        `,
        variables: {
          input: { attrs: input },
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error inviting user(s)');
  }

  public async getRoles() {
    const data = await this.query<GetRolesData>(
      () =>
        this.apolloService.executeQuery<GetRolesData>({
          query: gql`
            query roles {
              roles {
                id
                name
              }
            }
          `,
        }),
      'Error loading roles'
    );
    return data.roles;
  }

  public setRole(id: string, role: string) {
    return this.mutate<UserSchema>(async () => {
      const {
        data: { updateUserRole },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateUserRole($input: UpdateUserRoleInput!) {
            updateUserRole(input: $input) {
              errors
              message
              user {
                id
              }
            }
          }
        `,
        variables: {
          input: { id, role },
        },
      });
      return {
        data: updateUserRole.user,
        serverErrors: updateUserRole.errors,
        serverMessage: updateUserRole.message,
      };
    }, 'Error updating role');
  }

  public async getWelcomeMessageCover(email: string) {
    const data = await this.query<GetWelcomeMessageCoverData>(
      () =>
        this.apolloService.executeQuery<GetWelcomeMessageCoverData>({
          query: gql`
            query me {
              me {
                member {
                  coverKey
                  signedCover
                }
              }
            }
          `,
          variables: {
            email,
          },
        }),
      'Error loading user'
    );
    return data.me.member as Pick<MemberSchema, 'coverKey' | 'signedCover'>;
  }

  public async search(
    variables: SearchUsersInput,
    fields = userFields,
    fetchTotalCount = true
  ): Promise<ListingConnection<UserSchema>> {
    const response = await this.query<SearchUsersData>(
      () =>
        this.apolloService.executeQuery<SearchUsersData>({
          query: searchUsersQuery(fields, variables.ids, fetchTotalCount),
          variables,
        }),
      'Error loading users'
    );

    return mergeListingConnectionAliases(
      response.allUsers,
      response.usersByIds
    );
  }

  public async getMember(): Promise<UserSchema> {
    const response = await this.query(
      () =>
        this.apolloService.executeQuery<GetMeData>({
          query: gql`
            query me {
              me {
                photo
                member {
                  fromName
                  jobTitle
                  monthlyRecapEmail
                  roleDescription
                  vendorUserId
                  showInactives
                }
              }
            }
          `,
        }),
      'Error loading user'
    );

    return response.me;
  }
}
